/**
 * Portfolio-level cost charts (combined buildings + transport).
 * Semantic colors: favorable (#0f9f6e) / unfavorable (#c75b39). Sector colors: Buildings = blue, Transport = green.
 */
import React, { useMemo } from 'react';
import {
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  BarElement,
  BarController,
  Filler,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { currencyShort, FINANCIAL_COLORS } from './portfolioCostUtils';
import type { BuildingsCostResults } from '../../services/buildingsCostModel';
import type { TransportCostResults } from '../../services/transportCostModel';

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  BarElement,
  BarController,
  Tooltip,
  Legend,
  Filler
);

const axisTick = (tickValue: string | number): string | number =>
  typeof tickValue === 'number' ? tickValue.toLocaleString(undefined, { maximumFractionDigits: 0 }) : tickValue;

/** Build combined series by year from buildings + transport. */
export function usePortfolioSeries(
  buildings: BuildingsCostResults | null,
  transport: TransportCostResults | null
) {
  return useMemo(() => {
    const years: number[] = [];
    const bCumNet = new Map<number, number>();
    const bCapex = new Map<number, number>();
    const bSavings = new Map<number, number>();
    const tCumNet = new Map<number, number>();
    const tFuelSavings = new Map<number, number>();

    if (buildings?.cumulativeNetCostSeries?.length) {
      buildings.cumulativeNetCostSeries.forEach((r) => {
        years.push(r.year);
        bCumNet.set(r.year, r.value);
      });
      buildings.annualCapitalCostSeries?.forEach((r) => bCapex.set(r.year, r.value));
      buildings.annualSavingsSeries?.forEach((r) => bSavings.set(r.year, r.value));
    }
    if (transport?.cumulativeNetCostSeries?.length) {
      transport.cumulativeNetCostSeries.forEach((r) => {
        if (!years.includes(r.year)) years.push(r.year);
        tCumNet.set(r.year, r.value);
      });
      transport.annualFuelSavingsSeries?.forEach((r) => tFuelSavings.set(r.year, r.value));
    }
    years.sort((a, b) => a - b);
    if (years.length === 0) return null;

    const getPrev = (m: Map<number, number>, y: number) => {
      const i = years.indexOf(y);
      return i > 0 ? m.get(years[i - 1]) ?? 0 : 0;
    };
    const tCapexByYear = new Map<number, number>();
    years.forEach((y) => {
      const cum = tCumNet.get(y) ?? 0;
      const prevCum = getPrev(tCumNet, y);
      const fuel = tFuelSavings.get(y) ?? 0;
      tCapexByYear.set(y, cum - prevCum + fuel);
    });

    const cumulativeNetCost = years.map((y) => (bCumNet.get(y) ?? 0) + (tCumNet.get(y) ?? 0));
    const cumulativeCapex = years.map((y) => {
      let bSum = 0;
      let tSum = 0;
      for (const yy of years) {
        if (yy > y) break;
        bSum += bCapex.get(yy) ?? 0;
        tSum += tCapexByYear.get(yy) ?? 0;
      }
      return bSum + tSum;
    });
    const cumulativeSavings = years.map((y) => {
      let bSum = 0;
      let tSum = 0;
      for (const yy of years) {
        if (yy > y) break;
        bSum += bSavings.get(yy) ?? 0;
        tSum += tFuelSavings.get(yy) ?? 0;
      }
      return bSum + tSum;
    });
    const annualCapex = years.map((y) => (bCapex.get(y) ?? 0) + (tCapexByYear.get(y) ?? 0));
    const annualSavings = years.map((y) => (bSavings.get(y) ?? 0) + (tFuelSavings.get(y) ?? 0));
    // UI sign convention: positive values mean net savings.
    // net = operating impact (savings) - capital exp.
    const annualNetCashFlow = years.map((y, i) => annualSavings[i] - annualCapex[i]);

    return {
      years,
      labels: years.map(String),
      cumulativeNetCost,
      cumulativeCapex,
      cumulativeSavings,
      annualCapex,
      annualSavings,
      annualNetCashFlow,
      buildingsCapex: years.map((y) => bCapex.get(y) ?? 0),
      buildingsSavings: years.map((y) => bSavings.get(y) ?? 0),
      transportCapex: years.map((y) => tCapexByYear.get(y) ?? 0),
      transportSavings: years.map((y) => tFuelSavings.get(y) ?? 0),
    };
  }, [buildings, transport]);
}

export interface PortfolioCostChartsProps {
  buildings: BuildingsCostResults | null;
  transport: TransportCostResults | null;
}

/** A. Cumulative Net Cost (line) with optional cumulative capital exp. and savings. */
export const PortfolioCumulativeNetCostChart: React.FC<PortfolioCostChartsProps> = ({ buildings, transport }) => {
  const series = usePortfolioSeries(buildings, transport);
  const options: ChartOptions<'line'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { position: 'bottom' },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${currencyShort(Number(ctx.parsed.y))} (positive = net savings)`,
          },
        },
      },
      scales: {
        x: { title: { display: true, text: 'Year' } },
        y: {
          title: { display: true, text: 'Cumulative $' },
          ticks: { callback: axisTick },
        },
      },
    }),
    []
  );

  if (!series || series.years.length === 0) return null;
  const hasData =
    series.cumulativeNetCost.some((v) => Math.abs(v) >= 1) ||
    series.cumulativeCapex.some((v) => Math.abs(v) >= 1) ||
    series.cumulativeSavings.some((v) => Math.abs(v) >= 1);
  if (!hasData) return null;

  const data = {
    labels: series.labels,
    datasets: [
      { label: 'Cumulative net cost', data: series.cumulativeNetCost, borderColor: FINANCIAL_COLORS.neutral, backgroundColor: 'rgba(31, 41, 55, 0.08)', fill: true, tension: 0.2, pointRadius: 2 },
      { label: 'Cumulative capital exp.', data: series.cumulativeCapex, borderColor: FINANCIAL_COLORS.unfavorable, borderDash: [4, 2], tension: 0.2, pointRadius: 1 },
      { label: 'Cumulative savings', data: series.cumulativeSavings, borderColor: FINANCIAL_COLORS.favorable, borderDash: [4, 2], tension: 0.2, pointRadius: 1 },
    ],
  };

  return (
    <div style={{ marginTop: 16 }}>
      <h5 style={{ marginBottom: 8 }}>Cumulative Net Cost</h5>
      <p style={{ fontSize: '0.8125rem', color: '#6b7280', marginBottom: 8 }}>Combined buildings + transport. Negative = net savings.</p>
      <div style={{ height: 260 }}>
        <Line data={data} options={options} />
      </div>
    </div>
  );
};

/** B. Annual Cash Flow: bars (capital exp., savings), line (net). */
export const PortfolioAnnualCashFlowChart: React.FC<PortfolioCostChartsProps> = ({ buildings, transport }) => {
  const series = usePortfolioSeries(buildings, transport);
  const annualCapexDisplay = series ? series.annualCapex.map((v) => -Math.abs(v)) : [];
  const options: ChartOptions<'bar'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { position: 'bottom' },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const y = Number(ctx.parsed.y);
              const datasetLabel = ctx.dataset.label ?? '';
              if (datasetLabel.startsWith('Capital investment')) {
                return `${datasetLabel}: ${currencyShort(y)} (negative outflow)`;
              }
              if (datasetLabel.startsWith('Operating savings')) {
                return `${datasetLabel}: ${currencyShort(y)} (${y >= 0 ? 'savings' : 'cost increase'})`;
              }
              if (datasetLabel.startsWith('Net annual cash flow')) {
                return `${datasetLabel}: ${currencyShort(y)} (${y >= 0 ? 'positive net cash flow' : 'negative net cash flow'})`;
              }
              return `${datasetLabel}: ${currencyShort(y)}`;
            },
          },
        },
      },
      scales: {
        x: { title: { display: true, text: 'Year' }, stacked: false },
        y: { title: { display: true, text: '$' }, stacked: false, ticks: { callback: axisTick } },
      },
    }),
    []
  );

  if (!series || series.years.length === 0) return null;
  const hasData =
    series.annualCapex.some((v) => Math.abs(v) >= 1) ||
    series.annualSavings.some((v) => Math.abs(v) >= 1) ||
    series.annualNetCashFlow.some((v) => Math.abs(v) >= 1);
  if (!hasData) return null;

  const data = {
    labels: series.labels,
    datasets: [
      { label: 'Capital investment (−)', data: annualCapexDisplay, backgroundColor: 'rgba(199, 91, 57, 0.8)', yAxisID: 'y' },
      { label: 'Operating savings (+) / cost (−)', data: series.annualSavings, backgroundColor: 'rgba(15, 159, 110, 0.8)', yAxisID: 'y' },
      { label: 'Net annual cash flow', data: series.annualNetCashFlow, backgroundColor: 'rgba(30, 41, 59, 0.8)', yAxisID: 'y' },
    ],
  };

  return (
    <div style={{ marginTop: 16 }}>
      <h5 style={{ marginBottom: 8 }}>Annual Cash Flow</h5>
      <p style={{ fontSize: '0.8125rem', color: FINANCIAL_COLORS.muted, marginBottom: 8 }}>
        Capital investment is shown below zero. Operating savings are above zero; operating cost increases are below zero. Net annual cash flow = operating savings − capital investment.
      </p>
      <div style={{ height: 260 }}>
        <Bar data={data} options={options} />
      </div>
    </div>
  );
};

/** C. Cost Breakdown: stacked bar (buildings capital exp., transport capital exp., buildings savings, transport savings). */
export const PortfolioCostBreakdownChart: React.FC<PortfolioCostChartsProps> = ({ buildings, transport }) => {
  const series = usePortfolioSeries(buildings, transport);
  const options: ChartOptions<'bar'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: 'y' as const,
      plugins: {
        legend: { position: 'bottom' },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${currencyShort(Number(ctx.parsed.x))}`,
          },
        },
      },
      scales: {
        x: { stacked: true, ticks: { callback: axisTick } },
        y: { stacked: true },
      },
    }),
    []
  );

  if (!series || series.years.length === 0) return null;

  const totalBuildingsCapex = series.buildingsCapex.reduce((a, b) => a + b, 0);
  const totalTransportCapex = series.transportCapex.reduce((a, b) => a + b, 0);
  const totalBuildingsSavings = series.buildingsSavings.reduce((a, b) => a + b, 0);
  const totalTransportSavings = series.transportSavings.reduce((a, b) => a + b, 0);
  const hasData =
    Math.abs(totalBuildingsCapex) >= 1 ||
    Math.abs(totalTransportCapex) >= 1 ||
    Math.abs(totalBuildingsSavings) >= 1 ||
    Math.abs(totalTransportSavings) >= 1;
  if (!hasData) return null;

  // Sector identity: Buildings = blue, Transport = green (not good/bad)
  const data = {
    labels: ['Capital exp.', 'Savings'],
    datasets: [
      { label: 'Buildings capital exp.', data: [totalBuildingsCapex, 0], backgroundColor: 'rgba(59, 130, 246, 0.85)' },
      { label: 'Transport capital exp.', data: [totalTransportCapex, 0], backgroundColor: 'rgba(13, 148, 136, 0.85)' },
      { label: 'Buildings savings', data: [0, totalBuildingsSavings], backgroundColor: 'rgba(96, 165, 250, 0.85)' },
      { label: 'Transport savings', data: [0, totalTransportSavings], backgroundColor: 'rgba(45, 212, 191, 0.85)' },
    ],
  };

  return (
    <div style={{ marginTop: 16 }}>
      <h5 style={{ marginBottom: 8 }}>Cost Breakdown (Cumulative)</h5>
      <p style={{ fontSize: '0.8125rem', color: '#6b7280', marginBottom: 8 }}>Buildings vs transport capital exp. and savings over 2023–2045.</p>
      <div style={{ height: 140 }}>
        <Bar data={data} options={options} />
      </div>
    </div>
  );
};

/** Transport-only cumulative net cost (line). Shown in Transport tab. */
export const TransportCumulativeNetCostChart: React.FC<{ transport: TransportCostResults | null }> = ({ transport }) => {
  const series = usePortfolioSeries(null, transport);
  const options: ChartOptions<'line'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'bottom' },
        tooltip: {
          callbacks: {
            label: (ctx) => `${currencyShort(Number(ctx.parsed.y))} (negative = net savings)`,
          },
        },
      },
      scales: {
        x: { title: { display: true, text: 'Year' } },
        y: { title: { display: true, text: 'Cumulative $' }, ticks: { callback: axisTick } },
      },
    }),
    []
  );
  if (!series || series.years.length === 0) return null;
  const data = {
    labels: series.labels,
    datasets: [
      { label: 'Cumulative net cost (transport)', data: series.cumulativeNetCost, borderColor: 'rgb(124, 58, 237)', backgroundColor: 'rgba(124, 58, 237, 0.1)', fill: true, tension: 0.2, pointRadius: 2 },
    ],
  };
  return (
    <div style={{ marginTop: 16 }}>
      <h5 style={{ marginBottom: 8 }}>Transport cumulative net cost</h5>
      <p style={{ fontSize: '0.8125rem', color: '#6b7280', marginBottom: 8 }}>EV capital exp. − fuel savings by year. Negative = net savings.</p>
      <div style={{ height: 240 }}>
        <Line data={data} options={options} />
      </div>
    </div>
  );
};
